import type {
  LocalMarketAdKeyword,
  LocalMarketCompetitor,
  LocalMarketCompetitorDetail,
  LocalMarketKeywordOpportunity,
  LocalMarketKeywordsData,
} from '@elva/ui';
import { growthGet } from '@/data/http';
import type { BeLocalMarketList } from '@/data/marketingApiTypes';
import { num, str } from '@/data/adapt';

type BeLocalMarketCompetitor = NonNullable<BeLocalMarketList['competitors']>[number] & {
  est_monthly_spend?: number;
  ad_categories?: string[];
  rating?: number;
  review_count?: number;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? value : [];
}

function optionalText(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

function isMarketSummaryBlob(value: unknown): boolean {
  return isRecord(value) && ('ads_stats' in value || 'total_competitors' in value);
}

function extractCompetitorRows(be: Record<string, unknown>): BeLocalMarketCompetitor[] {
  const candidates = [
    be.competitors,
    be.items,
    be.results,
    ...(Array.isArray(be.competitors_included) ? [be.competitors_included] : []),
    (be.market_summary as Record<string, unknown> | undefined)?.competitors,
    (be.summary as Record<string, unknown> | undefined)?.competitors,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length > 0) {
      return candidate.filter((row) => isRecord(row) && !isMarketSummaryBlob(row)) as BeLocalMarketCompetitor[];
    }
  }

  return [];
}

function extractKeywordStrings(source: Record<string, unknown>): string[] {
  const ads = source.ads_stats as Record<string, unknown> | undefined;
  const fromAds = [
    ...asArray<unknown>(ads?.keywords_with_no_ads),
    ...asArray<unknown>(ads?.most_advertised_keywords),
  ]
    .map((item) => optionalText(item))
    .filter((item): item is string => Boolean(item));

  if (fromAds.length) return [...new Set(fromAds)];

  return asArray<unknown>(source.recommended)
    .map((row) => {
      if (!isRecord(row) || isMarketSummaryBlob(row)) return undefined;
      return optionalText(row.keyword ?? row.label ?? row.name);
    })
    .filter((item): item is string => Boolean(item));
}

function buildKeywordNarrative(source: Record<string, unknown>): string | undefined {
  const direct = optionalText(source.narrative);
  if (direct) return direct;

  if (!isMarketSummaryBlob(source)) return undefined;

  const ads = source.ads_stats as Record<string, unknown> | undefined;
  const noAds = asArray<unknown>(ads?.keywords_with_no_ads).length;
  if (noAds > 0) {
    return `${noAds} high-intent terms your competitors buy but you don't.`;
  }

  const total = num(source.total_competitors);
  if (total > 0) {
    return `Analysis across ${total} local competitors in your market radius.`;
  }

  return undefined;
}

function adaptRecommendedRow(row: unknown): LocalMarketKeywordOpportunity | null {
  if (!isRecord(row) || isMarketSummaryBlob(row)) return null;

  const keyword = optionalText(row.keyword ?? row.label ?? row.name);
  if (!keyword) return null;

  return {
    keyword,
    volume: num(row.volume ?? row.volume_monthly ?? row.search_volume_monthly),
    ...(row.cpc != null ? { cpc: num(row.cpc) } : {}),
    ...(optionalText(row.competition) ? { competition: optionalText(row.competition)! } : {}),
  };
}

function normalizeKeywordPayload(be: unknown): Record<string, unknown> {
  if (!isRecord(be)) return {};

  if (isMarketSummaryBlob(be)) return be;

  const nestedNarrative = be.narrative;
  if (isRecord(nestedNarrative) && isMarketSummaryBlob(nestedNarrative)) {
    return { ...be, ...nestedNarrative };
  }

  return be;
}

export interface LocalMarketData {
  clinicName: string;
  clinicAddress: string;
  centerCoordinate: { lat: number; lng: number } | null;
  competitors: LocalMarketCompetitor[];
}

function formatSpend(cents: number | null | undefined): string | null {
  if (cents == null || !Number.isFinite(cents)) return null;
  return `$${Math.round(cents / 100).toLocaleString()}`;
}

function adaptCoordinate(row: BeLocalMarketCompetitor): { lat: number; lng: number } | null {
  const lat = row.coordinate?.lat ?? row.lat;
  const lng = row.coordinate?.lng ?? row.lng;
  if (lat == null || lng == null || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat: num(lat), lng: num(lng) };
}

function adaptCompetitor(row: BeLocalMarketCompetitor): LocalMarketCompetitor {
  const categories = Array.isArray(row.ad_categories)
    ? row.ad_categories.map((item) => String(item)).filter(Boolean)
    : [];

  return {
    id: str(row.id ?? row.name, 'comp'),
    name: str(row.name),
    address: str(row.address, '—'),
    distanceMiles: num(row.distance_miles),
    coordinate: adaptCoordinate(row),
    estMonthlySpend: formatSpend(row.est_monthly_spend),
    adCategories: categories,
    ...(row.rating != null ? { rating: num(row.rating) } : {}),
    ...(row.review_count != null ? { reviewCount: num(row.review_count) } : {}),
  };
}

function adaptCenterCoordinate(
  center: BeLocalMarketList['center'],
): { lat: number; lng: number } | null {
  const lat = center?.coordinate?.lat;
  const lng = center?.coordinate?.lng;
  if (lat == null || lng == null || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat: num(lat), lng: num(lng) };
}

export async function fetchLocalMarket(radius?: number): Promise<LocalMarketData | undefined> {
  const params: Record<string, number> = { radius_miles: radius ?? 8 };

  const be = await growthGet<BeLocalMarketList & Record<string, unknown>>(
    '/local-market/competitors/',
    params,
  ).catch(() => undefined);
  if (!be) return undefined;

  const wire = isRecord(be) ? be : {};
  const center = (wire.center ?? undefined) as BeLocalMarketList['center'];
  const competitorRows = extractCompetitorRows(wire);

  return {
    clinicName: str(center?.clinic_name ?? wire.clinic_name, 'Your practice'),
    clinicAddress: str(center?.clinic_address ?? center?.address ?? wire.clinic_address, ''),
    centerCoordinate: adaptCenterCoordinate(center),
    competitors: competitorRows.map(adaptCompetitor),
  };
}

export async function fetchLocalMarketCompetitorDetail(
  id: string,
): Promise<LocalMarketCompetitorDetail | undefined> {
  const be = await growthGet<Record<string, unknown>>(
    `/local-market/competitors/${encodeURIComponent(id)}/`,
  ).catch(() => undefined);

  if (!be) return undefined;
  return adaptLocalMarketCompetitorDetail(be);
}

const AD_KEYWORD_TONES = new Set(['neutral', 'success', 'gold', 'warning', 'info', 'ai']);

function adaptHealthStatus(value: unknown): 'good' | 'bad' | 'warn' {
  const status = str(value, '').toLowerCase();
  if (status === 'good' || status === 'success') return 'good';
  if (status === 'bad' || status === 'error' || status === 'fail') return 'bad';
  return 'warn';
}

function adaptLocalMarketCompetitorDetail(be: Record<string, unknown>): LocalMarketCompetitorDetail {
  const googleAds = (be.google_ads ?? be.googleAds) as Record<string, unknown> | undefined;
  const seo = (be.seo ?? be.seo_organic) as Record<string, unknown> | undefined;

  const activeAdsRaw = (googleAds?.active_ads ?? googleAds?.activeAds) as
    | Array<Record<string, unknown>>
    | undefined;
  const topKeywordsRaw = (googleAds?.top_keywords ?? googleAds?.topKeywords) as
    | Array<Record<string, unknown>>
    | undefined;
  const rankedRaw = (seo?.ranked_keywords ?? seo?.rankedKeywords) as
    | Array<Record<string, unknown>>
    | undefined;
  const healthRaw = (seo?.website_health ?? seo?.websiteHealth) as
    | Array<Record<string, unknown>>
    | undefined;

  return {
    ...(be.rating != null ? { rating: num(be.rating) } : {}),
    ...(be.review_count != null || be.reviewCount != null
      ? { reviewCount: num(be.review_count ?? be.reviewCount) }
      : {}),
    ...(googleAds
      ? {
          googleAds: {
            ...(googleAds.est_monthly_spend != null || googleAds.estMonthlySpend != null
              ? {
                  estMonthlySpend: formatSpend(
                    num(googleAds.est_monthly_spend ?? googleAds.estMonthlySpend),
                  ),
                }
              : {}),
            ...(googleAds.spend_delta_pct != null || googleAds.spendDeltaPct != null
              ? { spendDeltaPct: num(googleAds.spend_delta_pct ?? googleAds.spendDeltaPct) }
              : {}),
            ...(googleAds.local_seo_rank != null || googleAds.localSeoRank != null
              ? { localSeoRank: num(googleAds.local_seo_rank ?? googleAds.localSeoRank) }
              : {}),
            ...(activeAdsRaw?.length
              ? {
                  activeAds: activeAdsRaw.map((row) => ({
                    ...(row.display_url ?? row.displayUrl
                      ? { displayUrl: str(row.display_url ?? row.displayUrl) }
                      : {}),
                    ...(row.headline ? { headline: str(row.headline) } : {}),
                    ...(row.body ? { body: str(row.body) } : {}),
                  })),
                }
              : {}),
            ...(topKeywordsRaw?.length
              ? {
                  topKeywords: topKeywordsRaw.map((row) => {
                    const toneRaw = str(row.tone, 'success');
                    const tone: LocalMarketAdKeyword['tone'] = AD_KEYWORD_TONES.has(toneRaw)
                      ? (toneRaw as NonNullable<LocalMarketAdKeyword['tone']>)
                      : 'success';
                    return { label: str(row.label ?? row.keyword), tone };
                  }),
                }
              : {}),
            ...(optionalText(googleAds.counter_strategy ?? googleAds.counterStrategy)
              ? {
                  counterStrategy: optionalText(
                    googleAds.counter_strategy ?? googleAds.counterStrategy,
                  )!,
                }
              : {}),
          },
        }
      : {}),
    ...(seo
      ? {
          seo: {
            ...(seo.est_organic_traffic != null || seo.estOrganicTraffic != null
              ? {
                  estOrganicTraffic: num(seo.est_organic_traffic ?? seo.estOrganicTraffic),
                }
              : {}),
            ...(seo.local_seo_score != null || seo.localSeoScore != null
              ? { localSeoScore: num(seo.local_seo_score ?? seo.localSeoScore) }
              : {}),
            ...(rankedRaw?.length
              ? {
                  rankedKeywords: rankedRaw.map((row) => ({
                    keyword: str(row.keyword),
                    rank: num(row.rank),
                  })),
                }
              : {}),
            ...(healthRaw?.length
              ? {
                  websiteHealth: healthRaw.map((row) => ({
                    label: str(row.label),
                    value: str(row.value),
                    status: adaptHealthStatus(row.status),
                  })),
                }
              : {}),
            ...(optionalText(seo.seo_opportunity ?? seo.seoOpportunity)
              ? {
                  seoOpportunity: optionalText(seo.seo_opportunity ?? seo.seoOpportunity)!,
                }
              : {}),
          },
        }
      : {}),
  };
}

export async function fetchLocalMarketKeywords(): Promise<LocalMarketKeywordsData | undefined> {
  const be = await growthGet<Record<string, unknown>>('/local-market/keyword-opportunities/').catch(
    () => undefined,
  );

  if (!be) return undefined;

  const source = normalizeKeywordPayload(be);
  const adaptedRows = asArray<unknown>(source.recommended)
    .map(adaptRecommendedRow)
    .filter((row): row is LocalMarketKeywordOpportunity => row != null);
  const fallbackKeywords = extractKeywordStrings(source);
  const recommended =
    adaptedRows.length > 0
      ? adaptedRows
      : fallbackKeywords.map((keyword) => ({ keyword, volume: 0 }));
  const narrative = buildKeywordNarrative(source);

  return {
    recommended,
    ...(source.analyzed_search_count != null
      ? { analyzedSearchCount: num(source.analyzed_search_count) }
      : {}),
    ...(narrative ? { narrative } : {}),
  };
}
